import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorText } from '../../lib/api';
import { useMe } from '../../lib/session';
import { fmtCountdown, fmtTime, useNow } from '../../lib/format';
import { dateToZonedInput, fmtBytes, openFile, TYPE_LABEL, zonedInputToDate, type Audience, type FileInfo, type ItemType, type LibraryItem } from '../../lib/library';
import { Button, Card, CardHeader, cx, Empty, ErrorText, Field, Input, Pill } from '../ui';
import { AudiencePicker } from './AudiencePicker';

const TYPE_TONE: Record<ItemType, string> = { VIDEO_LINK: 'text-live border-[#2e2650]', DOCUMENT: 'text-accent border-[#22385a]', STRATEGY: 'text-up border-[#1e5a3c]', RECORDING: 'text-warn border-[#5a4316]' };

export function TypeBadge({ type, className }: { type: ItemType; className?: string }) {
  return <span className={cx('inline-flex items-center rounded-md border bg-raised px-2 py-0.5 text-xs font-semibold', TYPE_TONE[type], className)}>{TYPE_LABEL[type]}</span>;
}

/** Privacy-enhanced embed (youtube-nocookie / player.vimeo — the only hosts the CSP allows), otherwise a plain link. */
export function VideoPlayer({ item }: { item: LibraryItem }) {
  const embed = item.video?.embedUrl;
  if (!embed) {
    return (
      <Card className="flex flex-col items-start gap-2 p-4">
        <span className="text-sub">This video plays on its own site.</span>
        {item.url && (
          <a href={item.url} target="_blank" rel="noopener noreferrer" className="font-semibold">
            Open the video ↗
          </a>
        )}
      </Card>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <div className="relative w-full overflow-hidden rounded-xl border border-line bg-black" style={{ aspectRatio: '16 / 9' }}>
        <iframe
          src={embed}
          title={`Video: ${item.title}`}
          className="absolute inset-0 h-full w-full border-0"
          loading="lazy"
          referrerPolicy="strict-origin-when-cross-origin"
          allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
        />
      </div>
      <span className="text-xs text-muted">
        {item.video?.provider === 'youtube' ? 'YouTube, privacy-enhanced mode (no cookies until you press play).' : 'Vimeo player.'}{' '}
        {item.url && (
          <a href={item.url} target="_blank" rel="noopener noreferrer">
            Open on {item.video?.provider === 'youtube' ? 'YouTube' : 'Vimeo'} ↗
          </a>
        )}
      </span>
    </div>
  );
}

/** An uploaded image, fetched through a signed link, with the viewer's name over it when the item asks for a watermark. */
export function SignedImage({ itemId, file, alt, watermark }: { itemId: string; file: FileInfo; alt: string; watermark?: boolean }) {
  const me = useMe();
  const link = useQuery({ queryKey: ['file-link', itemId, file.id], queryFn: () => api<{ url: string }>(`/library/items/${itemId}/files/${file.id}/link`, { body: {} }), staleTime: 4 * 60_000, refetchInterval: 4 * 60_000 });
  return (
    <div className="relative overflow-hidden rounded-lg border border-line bg-raised">
      {link.data ? <img src={link.data.url} alt={alt} className="block h-auto max-h-[70vh] w-full object-contain" /> : <div className="p-6 text-center text-sub">{link.error ? errorText(link.error) : 'Loading image…'}</div>}
      {watermark && me.data && (
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 flex flex-wrap content-around justify-around overflow-hidden opacity-25 select-none">
          {Array.from({ length: 6 }, (_, n) => (
            <span key={n} className="-rotate-12 font-semibold whitespace-nowrap text-ink">
              {me.data.name} · {me.data.email}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

const KIND_LABEL: Record<string, string> = { pdf: 'PDF', image: 'Image', word: 'Word document', excel: 'Excel workbook', powerpoint: 'PowerPoint deck', text: 'Text file' };

export function DocumentPanel({ item, staff }: { item: LibraryItem; staff: boolean }) {
  const [busy, setBusy] = useState<'view' | 'download' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const f = item.file;
  if (!f) return <Card className="p-4 text-sub">No file uploaded yet.</Card>;
  const canDownload = item.allowDownload || staff;
  const go = async (download: boolean) => {
    setBusy(download ? 'download' : 'view');
    setError(null);
    try {
      await openFile(item.id, f.id, download);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="flex flex-col gap-3">
      <Card className="flex flex-wrap items-center justify-between gap-3 p-4">
        <div className="flex min-w-0 items-center gap-3">
          <div aria-hidden="true" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-[#22385a] bg-accent-bg font-mono text-[11px] font-semibold text-accent uppercase">
            {f.filename.split('.').pop()?.slice(0, 4)}
          </div>
          <div className="flex min-w-0 flex-col">
            <span className="truncate font-semibold">{f.filename}</span>
            <span className="text-xs text-muted">
              {KIND_LABEL[f.kind ?? ''] ?? 'File'} · {fmtBytes(f.sizeBytes)}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {f.inline && f.kind === 'pdf' && (
            <Button variant="primary" onClick={() => void go(false)} disabled={!!busy}>
              {busy === 'view' ? 'Opening…' : 'Open PDF'}
            </Button>
          )}
          {canDownload && (
            <Button onClick={() => void go(true)} disabled={!!busy}>
              {busy === 'download' ? 'Preparing…' : 'Download'}
            </Button>
          )}
        </div>
      </Card>
      {!item.allowDownload && <span className="text-xs text-muted">{staff ? 'Downloading is switched off for traders.' : f.inline ? 'Downloading is switched off for this document; you can still view it.' : 'Downloading is switched off for this document. Ask its author if you need a copy.'}</span>}
      <ErrorText error={error} />
      {f.kind === 'image' && <SignedImage itemId={item.id} file={f} alt={item.title} watermark={item.watermark} />}
    </div>
  );
}

/** "I've finished this" with an optional note (INT-LIB-05). Also records the first time the item was opened. */
export function FinishedCard({ item, onChange }: { item: LibraryItem; onChange: () => void }) {
  const me = useMe();
  const tz = me.data?.timezone ?? 'UTC';
  const now = useNow(60_000);
  const [note, setNote] = useState('');
  const opened = useRef(false);
  const open = useMutation({ mutationFn: () => api(`/library/items/${item.id}/progress`, { body: { action: 'opened' } }) });
  const finish = useMutation({ mutationFn: () => api(`/library/items/${item.id}/progress`, { body: { action: 'completed', acknowledgement: note || null } }), onSuccess: onChange });
  useEffect(() => {
    if (!opened.current && !item.me.viewedAt) {
      opened.current = true;
      open.mutate();
    }
  }, [item.me.viewedAt, open]);
  const req = item.me.required;
  if (item.me.completedAt) {
    return (
      <Card tone="up" className="flex items-center gap-2 p-4">
        <span aria-hidden="true" className="text-up">✓</span>
        <span>
          You finished this on <strong>{fmtTime(item.me.completedAt, tz, true)}</strong>
          {req && new Date(item.me.completedAt).getTime() > new Date(req.dueAt).getTime() ? ' (after the due date)' : ''}.
        </span>
      </Card>
    );
  }
  return (
    <Card tone={req ? (req.overdue ? 'down' : 'warn') : undefined} className="flex flex-col gap-3 p-4">
      {req && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-semibold">Required learning</span>
          <Pill tone={req.overdue ? 'down' : 'warn'}>{req.overdue ? `Overdue ${fmtCountdown(req.dueAt, now).replace('overdue ', '')}` : `Due ${fmtTime(req.dueAt, tz, true)} · ${fmtCountdown(req.dueAt, now)}`}</Pill>
        </div>
      )}
      <Field label="Anything to add? (optional)">
        <Input value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Read and understood" />
      </Field>
      <ErrorText error={finish.error ? errorText(finish.error) : null} />
      <Button variant="success" className="self-start" onClick={() => finish.mutate()} disabled={finish.isPending}>
        {finish.isPending ? 'Saving…' : "I've finished this"}
      </Button>
    </Card>
  );
}

interface AssignmentsResp {
  summary: { assigned: number; finished: number; overdue: number; late: number };
  people: { assignmentId: string; user: { id: string; name: string } | null; market: string | null; dueAt: string; viewedAt: string | null; completedAt: string | null; late: boolean; overdue: boolean; acknowledgement: string | null }[];
}

/** Master Trader / Admin: make an item required for an audience, and see who has finished it (INT-LIB-05). */
export function RequiredLearningAdmin({ item, canAssign }: { item: LibraryItem; canAssign: boolean }) {
  const me = useMe();
  const qc = useQueryClient();
  const tz = me.data?.timezone ?? 'UTC';
  const [open, setOpen] = useState(false);
  const [audience, setAudience] = useState<Audience>({ floor: false, marketIds: [], userIds: [] });
  const [due, setDue] = useState('');
  const dueAt = zonedInputToDate(due, tz);
  const list = useQuery({ queryKey: ['library-assignments', item.id], queryFn: () => api<AssignmentsResp>(`/library/items/${item.id}/assignments`) });
  const assign = useMutation({
    mutationFn: () => api<{ assigned: number; alreadyAssigned: number }>(`/library/items/${item.id}/assignments`, { body: { audience, dueAt: dueAt?.toISOString() } }),
    onSuccess: () => {
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ['library-assignments', item.id] });
    },
  });
  const s = list.data?.summary;
  return (
    <Card>
      <CardHeader title="Required learning" right={s && s.assigned > 0 ? <span className="text-sm text-sub">{s.finished} of {s.assigned} finished{s.overdue ? ` · ${s.overdue} overdue` : ''}</span> : undefined} />
      {canAssign && item.status === 'PUBLISHED' && !open && (
        <div className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3">
          <Button
            onClick={() => {
              setDue(dateToZonedInput(new Date(Date.now() + 7 * 86400_000), tz));
              setOpen(true);
            }}
          >
            Make it required…
          </Button>
          {assign.data && (
            <span className="text-sm text-up" role="status">
              Assigned to {assign.data.assigned} trader{assign.data.assigned === 1 ? '' : 's'}
              {assign.data.alreadyAssigned ? ` · ${assign.data.alreadyAssigned} already had it` : ''}.
            </span>
          )}
        </div>
      )}
      {canAssign && item.status === 'PUBLISHED' && open && (
        <div className="flex flex-col gap-3 border-b border-line p-4">
          <AudiencePicker value={audience} onChange={setAudience} legend="Make it required for" />
          <div className="flex flex-wrap items-end gap-3">
            <Field label={`Due by (${tz} time)`}>
              <Input type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} />
            </Field>
            <Button variant="primary" onClick={() => assign.mutate()} disabled={assign.isPending || !dueAt || (!audience.floor && !audience.marketIds.length && !audience.userIds.length)}>
              {assign.isPending ? 'Assigning…' : 'Make required'}
            </Button>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
          <span className="text-xs text-muted">Each trader gets an alert now, reminders 3 days and 1 day before, and an overdue alert at the due time.</span>
          <ErrorText error={assign.error ? errorText(assign.error) : null} />
        </div>
      )}
      {canAssign && item.status !== 'PUBLISHED' && <p className="m-0 border-b border-line px-4 py-3 text-sm text-sub">Publish this item to make it required.</p>}
      {list.data && !list.data.people.length && <Empty>Not required for anyone yet.</Empty>}
      {!!list.data?.people.length && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[620px] border-collapse">
            <thead>
              <tr className="text-left text-xs text-muted">
                {['Trader', 'Due', 'Opened', 'Finished', 'Note'].map((h) => (
                  <th key={h} className="px-4 py-2.5 font-medium">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.data.people.map((p) => (
                <tr key={p.assignmentId} className="border-t border-[#1a1f27]">
                  <td className="px-4 py-2.5 font-semibold">
                    {p.user?.name ?? '—'} {p.market && <span className="text-xs font-normal text-muted">· {p.market}</span>}
                  </td>
                  <td className="px-4 py-2.5 text-sub">{fmtTime(p.dueAt, tz, true)}</td>
                  <td className="px-4 py-2.5 text-sub">{p.viewedAt ? fmtTime(p.viewedAt, tz, true) : '—'}</td>
                  <td className="px-4 py-2.5">{p.completedAt ? <Pill tone={p.late ? 'warn' : 'up'}>{p.late ? 'Late · ' : ''}{fmtTime(p.completedAt, tz, true)}</Pill> : p.overdue ? <Pill tone="down">Overdue</Pill> : <span className="text-muted">Not yet</span>}</td>
                  <td className="px-4 py-2.5 text-sub">{p.acknowledgement ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/** Publish / unpublish / archive buttons for editors. */
export function StatusActions({ item, onDone }: { item: LibraryItem; onDone: () => void }) {
  const act = useMutation({ mutationFn: (a: 'publish' | 'unpublish' | 'archive') => api(`/library/items/${item.id}/${a}`, { method: 'POST' }), onSuccess: onDone });
  return (
    <>
      {item.status !== 'PUBLISHED' && (
        <Button variant="primary" onClick={() => act.mutate('publish')} disabled={act.isPending}>
          Publish
        </Button>
      )}
      {item.status === 'PUBLISHED' && (
        <Button onClick={() => act.mutate('unpublish')} disabled={act.isPending}>
          Unpublish
        </Button>
      )}
      {item.status !== 'ARCHIVED' && (
        <Button variant="danger" onClick={() => act.mutate('archive')} disabled={act.isPending}>
          Archive
        </Button>
      )}
      {act.error && <ErrorText error={errorText(act.error)} />}
    </>
  );
}

export function StatusPill({ status }: { status: LibraryItem['status'] }) {
  if (status === 'PUBLISHED') return null;
  return <Pill tone={status === 'DRAFT' ? 'warn' : 'neutral'}>{status === 'DRAFT' ? 'Draft' : 'Archived'}</Pill>;
}

export function Prose({ text }: { text: string | null | undefined }) {
  if (!text) return null;
  return <p className="m-0 leading-relaxed whitespace-pre-wrap text-ink2">{text}</p>;
}

